import { useId, useState } from "react";
import "./MaskedField.css";

interface MaskedFieldProps {
  label: string;
  /** Argv-shaped values (a command, an entrypoint, a healthcheck `test`) — joined with a
   * space for display, matching how an operator would read it back as a shell line. */
  values: string[] | undefined;
  /** Shown instead of the mask/reveal control when `values` is empty/absent — there is
   * nothing sensitive to guard in that case. */
  emptyLabel: string;
}

/**
 * Renders a SENSITIVE, verbatim-captured field (a service's `command`/`entrypoint`/
 * `healthcheck.test` — ADR-0038 §4) masked by default with an explicit per-field reveal,
 * exactly like a secret value gets the same treatment elsewhere in this app.
 *
 * Security posture, not just UX (see `Service`'s doc comment in types.ts):
 *   - The real value is NEVER placed in the DOM while masked — the placeholder glyphs
 *     are a static string, not a CSS-hidden copy of the value. A screen reader, a
 *     "Copy" browser extension, or a test querying accessible text all see only the
 *     placeholder until the operator explicitly reveals it.
 *   - The reveal button's accessible name never includes the value itself (no value in
 *     `aria-label`/`title`/tooltip — those can leak on hover independent of the masked
 *     rendering).
 *   - This component never logs anything — there is nothing here that could console.log
 *     the value even accidentally.
 */
export function MaskedField({ label, values, emptyLabel }: MaskedFieldProps) {
  const [revealed, setRevealed] = useState(false);
  const labelId = useId();

  const hasValue = Boolean(values && values.length > 0);

  return (
    <div className="masked-field">
      <div className="masked-field__row">
        <span className="masked-field__label" id={labelId}>
          {label}
        </span>
        {hasValue && (
          <button
            type="button"
            className="masked-field__toggle"
            aria-pressed={revealed}
            aria-describedby={labelId}
            onClick={() => setRevealed((r) => !r)}
          >
            {revealed ? "Hide" : "Reveal"}
          </button>
        )}
      </div>
      {!hasValue ? (
        <span className="masked-field__empty">{emptyLabel}</span>
      ) : revealed ? (
        <code className="masked-field__value">{(values ?? []).join(" ")}</code>
      ) : (
        <code className="masked-field__value masked-field__value--masked">••••••••••••</code>
      )}
    </div>
  );
}
